import { useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { MessageSquare, RefreshCw, Settings, Wand2 } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { clsx } from 'clsx';
import { PageHeader } from '@/shared/ui/PageHeader';
import { EmptyState } from '@/shared/ui/EmptyState';
import { Button } from '@/shared/ui/Button';
import { SkeletonCard } from '@/shared/ui/Skeleton';
import patterns from '@/shared/ui/styles/patterns.module.css';
import { useLocation } from '@/shared/hooks/useLocation';
import { integrationsApi } from '@/modules/settings/api/integrationsApi';
import { integrationKeys } from '@/modules/settings/queryKeys';
import { useReviews } from '../hooks/useReviews';
import { useReviewStats } from '../hooks/useReviewStats';
import { useReviewActions } from '../hooks/useReviewActions';
import { ReviewCard, ReviewResponseModal, CockpitTab, CaptureTab } from '../components';
import type { Review, ReviewStats } from '../api/reviewsApi';
import styles from './ReviewsPage.module.css';

type Tab = 'overview' | 'respond' | 'capture';

const TABS: { value: Tab; label: string }[] = [
  { value: 'overview', label: 'Overview' },
  { value: 'respond', label: 'Respond' },
  { value: 'capture', label: 'Get reviews' },
];

// Text requests are one way of getting reviews, so they live inside Get reviews (CaptureTab).
// An old ?tab=text link lands there too.
function isTab(value: string | null): value is Tab {
  return value === 'overview' || value === 'respond' || value === 'capture';
}

function syncSuffix(stats: ReviewStats | undefined): string {
  const n = stats?.lastSyncNewCount;
  if (n == null) return '';
  return n === 0 ? ' · No new reviews' : ` · Found ${n} new review${n === 1 ? '' : 's'}`;
}

export function ReviewsPage() {
  const { currentLocation } = useLocation();
  const locationId = currentLocation?.id ?? '';

  // Respond tab always shows unresponded only
  const { reviews, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage } =
    useReviews('unresponded');
  const { data: stats } = useReviewStats();
  const { sync, regenerateAll } = useReviewActions();
  const [active, setActive] = useState<Review | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const requested = searchParams.get('tab');
  const tab: Tab = requested === 'text' ? 'capture' : isTab(requested) ? requested : 'overview';
  // Refresh, Regenerate drafts and Settings are about Google reviews and replies, not asking for
  // new reviews: they show on Overview and Respond only.
  const replyTab = tab === 'overview' || tab === 'respond';
  const setTab = (next: Tab) =>
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      if (next === 'overview') params.delete('tab');
      else params.set('tab', next);
      return params;
    });

  const { data: gbpStatus } = useQuery({
    queryKey: integrationKeys.gbpStatus(locationId),
    queryFn: () => integrationsApi.gbp.getStatus(locationId).then((r) => r.data),
    enabled: !!locationId,
    staleTime: 5 * 60 * 1000,
  });
  const gbpConnected = gbpStatus?.connected ?? false;

  return (
    <div className={patterns.page}>
      <PageHeader
        title="Reviews"
        subtitle="Manage your Google reviews — see where you stand, respond in your voice, and grow new reviews."
        timestamp={stats?.lastSyncedAt ?? undefined}
        timestampSuffix={syncSuffix(stats)}
        actions={
          replyTab ? (
            <div className={styles.headerActions}>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => sync.mutate()}
                isLoading={sync.isPending}
              >
                <RefreshCw size={14} aria-hidden /> Refresh
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => regenerateAll.mutate()}
                isLoading={regenerateAll.isPending}
              >
                <Wand2 size={14} aria-hidden /> Regenerate drafts
              </Button>
              <Button variant="secondary" size="sm" onClick={() => navigate('/settings/organic')}>
                <Settings size={14} aria-hidden /> Settings
              </Button>
            </div>
          ) : undefined
        }
      />

      <div className={patterns.filterGroup}>
        {TABS.map((t) => (
          <button
            key={t.value}
            type="button"
            className={clsx(patterns.filterPill, tab === t.value && patterns.filterPillActive)}
            onClick={() => setTab(t.value)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'overview' && <CockpitTab gbpConnected={gbpConnected} onRespond={setActive} />}

      {tab === 'capture' && <CaptureTab />}

      {tab === 'respond' && (
        <>
          {isLoading ? (
            <div className={styles.list}>
              <SkeletonCard />
              <SkeletonCard />
              <SkeletonCard />
            </div>
          ) : reviews.length === 0 ? (
            <EmptyState
              icon={<MessageSquare size={28} aria-hidden />}
              title="All caught up"
              description="You've responded to every review. Nice work."
            />
          ) : (
            <>
              <div className={styles.list}>
                {reviews.map((review) => (
                  <ReviewCard key={review.id} review={review} onRespond={setActive} />
                ))}
              </div>
              {hasNextPage && (
                <div className={styles.loadMore}>
                  <Button
                    variant="secondary"
                    onClick={() => fetchNextPage()}
                    isLoading={isFetchingNextPage}
                  >
                    Load more
                  </Button>
                </div>
              )}
            </>
          )}
        </>
      )}

      <ReviewResponseModal review={active} isOpen={!!active} onClose={() => setActive(null)} />
    </div>
  );
}
